import React, { useState, useEffect } from 'react'
import AdminDashboard from './AdminDashboard'
import MeetingRoomManager from './MeetingRoomManager'
import { alsokTheme, componentThemes } from '../styles/alsok-theme'
import { loadReceptionStatus, saveReceptionStatus } from '../utils/receptionStatusDefaults'
import { receptionStatusManager } from '../services/receptionStatusManager'
import { useReceptionStatus } from '../hooks/useReceptionStatus'

const AdminPanel = ({ user, onStartReception, onLogout }) => {
  console.log('🔧 AdminPanel initialized with onStartReception:', onStartReception) // デバッグログ
  console.log('🔧 AdminPanel onStartReception type:', typeof onStartReception) // デバッグログ
  console.log('🔧 AdminPanel onStartReception name:', onStartReception?.name) // デバッグログ
  console.log('🔧 AdminPanel onStartReception toString:', onStartReception?.toString().substring(0, 100)) // デバッグログ
  
  const [currentView, setCurrentView] = useState('dashboard')
  const [systemStatus, setSystemStatus] = useState(null)
  const [envStatus, setEnvStatus] = useState(null)
  
  // グローバル状態管理フックを使用
  const receptionStatusHook = useReceptionStatus()
  const receptionStatus = receptionStatusHook.status
  
  // 従来の状態変数は派生状態として計算
  const isReceptionActive = receptionStatus.status === 'active'
  const systemMode = receptionStatus.systemMode
  const maintenanceMessage = receptionStatus.maintenanceMessage
  
  // ローカル状態
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false)
  const [tempMaintenanceMessage, setTempMaintenanceMessage] = useState('システムメンテナンス中です。しばらくお待ちください。')
  const [confirmDialog, setConfirmDialog] = useState({ show: false, action: null, message: '', step: 1 })
  const [realTimeData, setRealTimeData] = useState({
    todayVisitors: 12,
    currentMeetings: 3,
    systemLoad: 65,
    lastUpdate: new Date(),
    weeklyVisitors: 87,
    monthlyVisitors: 324,
    // 新しい統計項目
    faceAuthCount: 9,
    walkInVisitors: 3,
    roomUsageCount: 5,
    peakHour: 10
  })
  const [notifications, setNotifications] = useState([])
  const [toastNotifications, setToastNotifications] = useState([])
  
  // 稼働時間の管理
  const [operationStartTime, setOperationStartTime] = useState(null)
  const [operationDuration, setOperationDuration] = useState(0)

  useEffect(() => {
    fetchSystemStatus()
    fetchEnvStatus()
    
    // 初期状態での稼働時間設定
    const globalStatus = receptionStatusManager.getStatus()
    if (globalStatus.status === 'active' && globalStatus.systemMode === 'normal') {
      console.log('⏰ 初期化時に稼働状態を検出、稼働時間カウント開始')
      setOperationStartTime(new Date())
      setOperationDuration(0)
    }
    
    // グローバル状態変更の監視
    console.log('📡 AdminPanel: グローバル状態リスナーを登録中...')
    const unsubscribe = receptionStatusManager.addListener((event) => {
      console.log('🔄 AdminPanel: グローバル状態変更を受信:', event.current)
      
      const wasActive = receptionStatus.status === 'active'
      const isNowActive = event.current.status === 'active'
      const isNormalMode = event.current.systemMode === 'normal'
      
      // 状態はグローバルフックから取得されるので、ローカル状態の更新は不要
      // setIsReceptionActive(isNowActive)
      // setSystemMode(event.current.systemMode)
      // setMaintenanceMessage(event.current.maintenanceMessage)
      
      // 稼働時間の管理
      if (!wasActive && isNowActive && isNormalMode) {
        // 停止→稼働（通常モード）: 開始時刻を記録
        console.log('⏰ 稼働時間カウント開始')
        setOperationStartTime(new Date())
        setOperationDuration(0)
      } else if ((wasActive && !isNowActive) || !isNormalMode) {
        // 稼働→停止 または メンテナンスモード: リセット
        console.log('⏰ 稼働時間リセット')
        setOperationStartTime(null)
        setOperationDuration(0)
      }
    })
    
    // リアルタイムデータの更新
    const updateRealTimeData = () => {
      setRealTimeData(prev => {
        const newTodayVisitors = Math.floor(Math.random() * 50) + 10
        const newFaceAuthCount = Math.floor(newTodayVisitors * 0.7) + Math.floor(Math.random() * 5)
        const newWalkInVisitors = Math.max(0, newTodayVisitors - newFaceAuthCount - Math.floor(Math.random() * 3))
        const newRoomUsageCount = Math.floor(Math.random() * 8) + 2
        const newPeakHour = Math.floor(Math.random() * 8) + 9 // 9時〜16時
        
        return {
          ...prev,
          todayVisitors: newTodayVisitors,
          currentMeetings: Math.floor(Math.random() * 6) + 1,
          systemLoad: Math.floor(Math.random() * 30) + 40,
          lastUpdate: new Date(),
          faceAuthCount: newFaceAuthCount,
          walkInVisitors: newWalkInVisitors,
          roomUsageCount: newRoomUsageCount,
          peakHour: newPeakHour
        }
      })
    }
    
    const interval = setInterval(updateRealTimeData, 30000) // 30秒ごとに更新
    
    return () => {
      console.log('🗑️ AdminPanel: クリーンアップ中...')
      unsubscribe()
      clearInterval(interval)
    }
  }, [])

  // 稼働時間の更新タイマー
  useEffect(() => {
    let timer
    if (operationStartTime && isReceptionActive && systemMode === 'normal') {
      timer = setInterval(() => {
        const now = new Date()
        const durationInSeconds = Math.floor((now - operationStartTime) / 1000)
        setOperationDuration(durationInSeconds)
      }, 1000)
    }
    
    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [operationStartTime, isReceptionActive, systemMode])

  // 通知システム初期化とイベントリスナー
  useEffect(() => {
    // 既存の通知を読み込み
    const existingNotifications = JSON.parse(localStorage.getItem('adminNotifications') || '[]')
    setNotifications(existingNotifications)

    // adminNotificationイベントリスナー
    const handleAdminNotification = (event) => {
      const newNotification = event.detail
      console.log('Admin Panel notification received:', newNotification) // デバッグログ
      
      setNotifications(prev => {
        const updatedNotifications = [newNotification, ...prev]
        // localStorageにも保存
        localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
        return updatedNotifications
      })
    }

    window.addEventListener('adminNotification', handleAdminNotification)
    return () => window.removeEventListener('adminNotification', handleAdminNotification)
  }, [])

  const fetchSystemStatus = async () => {
    try {
      const response = await fetch('http://localhost:5001/api/system-status')
      if (!response.ok) {
        console.warn('System status API returned error:', response.status)
        // ダミーデータを設定してUIを動作させる
        setSystemStatus({
          success: true,
          system: {
            uptime: 86400,
            database: 'ok'
          },
          today: {
            total_visitors: 0,
            face_auth_visitors: 0,
            rooms_used: 0
          }
        })
        return
      }
      const data = await response.json()
      if (data.success) {
        setSystemStatus(data)
      }
    } catch (error) {
      console.error('Error fetching system status:', error)
      // ダミーデータを設定してUIを動作させる
      setSystemStatus({
        success: true,
        system: {
          uptime: 86400,
          database: 'ok'
        },
        today: {
          total_visitors: 0,
          face_auth_visitors: 0,
          rooms_used: 0
        }
      })
    }
  }

  const fetchEnvStatus = async () => {
    try {
      const response = await fetch('http://localhost:5001/api/env-status')
      if (!response.ok) {
        console.warn('Environment status API returned error:', response.status)
        // ダミーデータを設定
        setEnvStatus({
          azureFaceApi: { configured: false },
          azureSpeechApi: { configured: false },
          microsoftGraphApi: { configured: false },
          emailSettings: { configured: false },
          teamsWebhook: { configured: false }
        })
        return
      }
      const data = await response.json()
      if (data.success) {
        setEnvStatus(data.envStatus)
      }
    } catch (error) {
      console.error('Error fetching environment status:', error)
      // ダミーデータを設定
      setEnvStatus({
        azureFaceApi: { configured: false },
        azureSpeechApi: { configured: false },
        microsoftGraphApi: { configured: false },
        emailSettings: { configured: false },
        teamsWebhook: { configured: false }
      })
    }
  }


  // 2段階確認ダイアログ処理
  const showConfirmDialog = (action, message) => {
    setConfirmDialog({
      show: true,
      action,
      message,
      step: 1
    })
  }

  const handleConfirmStep = () => {
    if (confirmDialog.step === 1) {
      setConfirmDialog(prev => ({ ...prev, step: 2 }))
    } else {
      // 2回目の確認後に実行
      if (confirmDialog.action === 'start') {
        handleReceptionStatusChange('active')
      } else if (confirmDialog.action === 'stop') {
        handleReceptionStatusChange('stopped')
      }
      setConfirmDialog({ show: false, action: null, message: '', step: 1 })
    }
  }

  const handleConfirmCancel = () => {
    setConfirmDialog({ show: false, action: null, message: '', step: 1 })
  }

  // 受付状態表示のヘルパー関数
  const getReceptionStatusClass = () => {
    if (systemMode === 'maintenance') {
      return 'maintenance'
    }
    return isReceptionActive ? 'active' : 'inactive'
  }

  const getReceptionStatusText = () => {
    if (systemMode === 'maintenance') {
      return '🔧 メンテナンス'
    }
    return isReceptionActive ? '🟢 通常' : '🔴 停止'
  }

  // 受付状態変更の共通処理
  const handleReceptionStatusChange = async (newStatus, options = {}) => {
    try {
      console.log(`🔄 受付状態を「${newStatus}」に変更中...`)
      
      // グローバル状態を更新（これにより全コンポーネントに自動で同期される）
      const success = receptionStatusManager.updateStatus(newStatus, {
        ...options,
        source: 'AdminPanel'
      })
      
      if (success) {
        // トースト通知を表示
        if (newStatus === 'active') {
          showToastNotification('受付システムが開始されました', 'success')
        } else if (newStatus === 'stopped') {
          showToastNotification('受付システムが停止されました', 'warning')
        } else if (newStatus === 'maintenance') {
          showToastNotification('受付状態がメンテナンス中に変更されました', 'warning')
        }
        
        console.log(`✅ 受付状態の変更完了: ${newStatus}`)
      } else {
        throw new Error('グローバル状態の更新に失敗')
      }
    } catch (error) {
      console.error('❌ 受付状態変更エラー:', error)
      showToastNotification('受付状態の変更に失敗しました', 'error')
    }
  }

  // トースト通知機能
  const showToastNotification = (message, type = 'info') => {
    const id = Date.now()
    const toast = {
      id,
      message,
      type, // 'success', 'warning', 'error', 'info'
      timestamp: new Date()
    }
    
    setToastNotifications(prev => [...prev, toast])
    
    // 3秒後に自動削除
    setTimeout(() => {
      setToastNotifications(prev => prev.filter(t => t.id !== id))
    }, 3000)
  }

  const removeToastNotification = (id) => {
    setToastNotifications(prev => prev.filter(t => t.id !== id))
  }

  const handleStartReception = async () => {
    // メンテナンス中の場合は、先にメンテナンスモードを終了
    if (systemMode === 'maintenance') {
      await handleNormalMode()
    }
    showConfirmDialog('start', '受付システムを開始しますか？\n\nシステムが稼働状態になり、来客対応が開始されます。')
  }

  const handleStopReception = async () => {
    // メンテナンス中の場合は、先にメンテナンスモードを終了
    if (systemMode === 'maintenance') {
      await handleNormalMode()
    }
    showConfirmDialog('stop', '受付システムを停止しますか？\n\n来客対応が停止され、システムが待機状態になります。')
  }


  // メンテナンスモード設定
  const handleMaintenanceMode = async () => {
    setShowMaintenanceDialog(true)
  }

  const handleConfirmMaintenance = async () => {
    try {
      console.log('🔧 メンテナンスモードを開始中...')
      
      // グローバル状態でメンテナンスモードを開始
      const success = receptionStatusManager.startMaintenance(tempMaintenanceMessage)
      
      if (success) {
        showToastNotification('メンテナンスモードが有効になりました', 'warning')
        console.log('✅ メンテナンスモード開始完了')
        setShowMaintenanceDialog(false)
      } else {
        throw new Error('メンテナンスモードの開始に失敗')
      }
    } catch (error) {
      console.error('❌ メンテナンスモード開始エラー:', error)
      showToastNotification('メンテナンスモードの開始に失敗しました', 'error')
    }
  }

  const handleCancelMaintenance = () => {
    setShowMaintenanceDialog(false)
    setTempMaintenanceMessage('システムメンテナンス中です。しばらくお待ちください。')
  }

  // 通常モードに戻す
  const handleNormalMode = async () => {
    try {
      console.log('🔧 メンテナンスモード終了中...')
      
      // グローバル状態でメンテナンスモードを終了
      const success = receptionStatusManager.stopMaintenance()
      
      if (success) {
        showToastNotification('メンテナンスモードを終了しました', 'info')
        console.log('✅ メンテナンスモード終了完了')
      } else {
        throw new Error('メンテナンスモードの終了に失敗')
      }
    } catch (error) {
      console.error('❌ メンテナンスモード終了エラー:', error)
      showToastNotification('メンテナンスモードの終了に失敗しました', 'error')
    }
  }

  // モード遷移機能
  const handleModeTransition = (mode) => {
    console.log('🚀 AdminPanel - handleModeTransition called with mode:', mode) // デバッグログ
    console.log('🚀 AdminPanel - onStartReception exists:', !!onStartReception) // デバッグログ
    console.log('🚀 AdminPanel - onStartReception type:', typeof onStartReception) // デバッグログ
    
    if (mode === 'admin-reception') {
      console.log('🚀 AdminPanel - Calling onStartReception with admin mode') // デバッグログ
      if (onStartReception) {
        console.log('🚀 AdminPanel - About to call onStartReception("admin")') // デバッグログ
        try {
          onStartReception('admin')
          console.log('🚀 AdminPanel - onStartReception("admin") completed successfully') // デバッグログ
        } catch (error) {
          console.error('🚀 AdminPanel - Error calling onStartReception:', error) // デバッグログ
        }
      } else {
        console.error('🚀 AdminPanel - onStartReception is null/undefined!')
      }
    } else if (mode === 'customer-reception') {
      console.log('🚀 AdminPanel - Calling onStartReception with customer mode') // デバッグログ
      if (onStartReception) {
        onStartReception('customer')
      } else {
        console.error('🚀 AdminPanel - onStartReception is null/undefined!')
      }
    } else {
      console.log('🚀 AdminPanel - Unknown mode:', mode) // デバッグログ
    }
  }

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${days}日 ${hours}時間 ${minutes}分`
  }

  // 稼働時間のフォーマット（時:分表示）
  const formatOperationTime = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  // 通知処理関数
  const handleNotificationRead = (notificationId) => {
    const updatedNotifications = notifications.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    )
    setNotifications(updatedNotifications)
    localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
  }

  const handleNotificationDismiss = (notificationId) => {
    const updatedNotifications = notifications.filter(n => n.id !== notificationId)
    setNotifications(updatedNotifications)
    localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
  }

  const clearAllNotifications = () => {
    setNotifications([])
    localStorage.setItem('adminNotifications', JSON.stringify([]))
  }

  if (currentView === 'full-dashboard') {
    return <AdminDashboard onClose={() => setCurrentView('dashboard')} />
  }

  if (currentView === 'meeting-room-manager') {
    return <MeetingRoomManager onClose={() => setCurrentView('dashboard')} />
  }

  return (
    <div className="admin-panel">
      {/* 改善されたヘッダー */}
      <div className="admin-header">
        <div className="admin-header-left">
          <h1>🛡️ ALSOK AI受付まもりちゃん 管理画面</h1>
        </div>
        
        <div className="admin-header-right">
          {/* 通知カウンター */}
          {notifications.filter(n => !n.read).length > 0 && (
            <div className="notification-badge">
              <span className="notification-icon">🔔</span>
              <span className="notification-count">{notifications.filter(n => !n.read).length}</span>
            </div>
          )}
          
          <div className="user-info">
            <span className="user-avatar">{user.role === 'administrator' ? '👑' : user.role === 'staff' ? '👤' : '🎯'}</span>
            <div className="user-details">
              <div className="user-name">{user.name}</div>
              <div className="user-role">({user.role})</div>
            </div>
            <div className="login-time">
              ログイン: {new Date(user.loginTime).toLocaleString('ja-JP')}
            </div>
          </div>
          <button onClick={onLogout} className="logout-button">
            🚪 ログアウト
          </button>
        </div>
      </div>

      <div className="admin-content">
        {/* システムモード表示は、受付状態ですべて管理するため非表示 */}

        {/* 横3分割レイアウト - 最上部に配置 */}
        <div className="status-overview-section">
          <div className="status-overview-grid">
            {/* 左エリア: 受付状態表示と操作 */}
            <div className="status-area reception-area">
              <h3 className="area-title">🎯 受付状態</h3>
              <div className="reception-status-display">
                <div className="status-main">
                  <div className={`status-indicator-large ${getReceptionStatusClass()}`}>
                    <span className="status-dot"></span>
                    <span className="status-text">{getReceptionStatusText()}</span>
                  </div>
                  <div className="status-time">
                    <span className="time-label">稼働時間</span>
                    <span className="time-value">
                      {isReceptionActive && systemMode === 'normal' 
                        ? formatOperationTime(operationDuration)
                        : '00:00'
                      }
                    </span>
                  </div>
                </div>
                <div className="status-controls-enhanced">
                  <button 
                    onClick={handleStartReception}
                    className={`control-btn-enhanced normal ${isReceptionActive && systemMode === 'normal' ? 'active' : ''}`}
                    disabled={isReceptionActive && systemMode === 'normal'}
                    title="通常モード（受付開始）"
                  >
                    <div className="btn-icon-enhanced">▶</div>
                    <div className="btn-label-enhanced">通常</div>
                  </button>
                  <button 
                    onClick={handleStopReception}
                    className={`control-btn-enhanced stop ${!isReceptionActive && systemMode === 'normal' ? 'active' : ''}`}
                    disabled={!isReceptionActive && systemMode === 'normal'}
                    title="停止モード（受付停止）"
                  >
                    <div className="btn-icon-enhanced">■</div>
                    <div className="btn-label-enhanced">停止</div>
                  </button>
                  <button 
                    onClick={handleMaintenanceMode}
                    className={`control-btn-enhanced maintenance ${systemMode === 'maintenance' ? 'active' : ''}`}
                    disabled={systemMode === 'maintenance'}
                    title="メンテナンスモード開始"
                  >
                    <div className="btn-icon-enhanced">🔧</div>
                    <div className="btn-label-enhanced">メンテナンス</div>
                  </button>
                </div>
              </div>
            </div>

            {/* 中央エリア: 来訪者・会議関連情報 */}
            <div className="status-area visitor-area">
              <h3 className="area-title">👥 来訪者・会議情報</h3>
              <div className="compact-stats">
                <div className="compact-stat">
                  <span className="stat-icon">👥</span>
                  <div className="stat-info">
                    <span className="stat-value">{realTimeData.todayVisitors}人</span>
                    <span className="stat-label">本日の来訪者</span>
                  </div>
                </div>
                <div className="compact-stat">
                  <span className="stat-icon">📅</span>
                  <div className="stat-info">
                    <span className="stat-value">{realTimeData.roomUsageCount}件</span>
                    <span className="stat-label">会議室予約</span>
                  </div>
                </div>
                <div className="compact-stat">
                  <span className="stat-icon">🏢</span>
                  <div className="stat-info">
                    <span className="stat-value">{realTimeData.currentMeetings}件</span>
                    <span className="stat-label">現在の会議</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 右エリア: サーバー/システム状態 */}
            <div className="status-area system-area">
              <h3 className="area-title">🖥️ システム状態</h3>
              <div className="system-stats">
                <div className="system-stat">
                  <span className="system-icon">💾</span>
                  <div className="system-info">
                    <span className="system-label">DB接続</span>
                    <span className={`system-status ${systemStatus?.system?.database === 'ok' ? 'ok' : 'error'}`}>
                      {systemStatus?.system?.database === 'ok' ? '○ 正常' : '× 切断'}
                    </span>
                  </div>
                </div>
                <div className="system-stat">
                  <span className="system-icon">🔐</span>
                  <div className="system-info">
                    <span className="system-label">認証サービス</span>
                    <span className={`system-status ${envStatus?.azureFaceApi?.configured ? 'ok' : 'error'}`}>
                      {envStatus?.azureFaceApi?.configured ? '○ 正常' : '× 未設定'}
                    </span>
                  </div>
                </div>
                <div className="system-stat">
                  <span className="system-icon">📡</span>
                  <div className="system-info">
                    <span className="system-label">レイテンシ</span>
                    <span className="system-status ok">125ms</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 操作エリア */}
        <div className="operations-section">
          <h2 className="section-title">📋 主要操作</h2>
          

          {/* 統一された操作グリッド */}
          <div className="operation-grid">
            <button 
              onClick={() => setCurrentView('full-dashboard')}
              className="operation-card primary"
            >
              <div className="card-icon">🚀</div>
              <div className="card-content">
                <h3>詳細管理画面</h3>
                <p>ログ管理・統計・設定確認</p>
              </div>
            </button>

            <button 
              onClick={() => {
                console.log('🔴 管理者受付モードボタン clicked!')
                handleModeTransition('admin-reception')
              }}
              className="operation-card secondary"
            >
              <div className="card-icon">👨‍💼</div>
              <div className="card-content">
                <h3>管理者受付モード</h3>
                <p>管理者用受付画面に遷移</p>
              </div>
            </button>

            <button 
              onClick={() => {
                console.log('🔵 お客様受付モードボタン clicked!')
                handleModeTransition('customer-reception')
              }}
              className="operation-card secondary"
            >
              <div className="card-icon">👥</div>
              <div className="card-content">
                <h3>お客様受付モード</h3>
                <p>お客様用受付画面に遷移</p>
              </div>
            </button>

            <button 
              onClick={() => setCurrentView('meeting-room-manager')}
              className="operation-card warning"
            >
              <div className="card-icon">🏢</div>
              <div className="card-content">
                <h3>会議室管理</h3>
                <p>予約・変更・削除・空室確認</p>
              </div>
            </button>

            {user.role === 'administrator' && (
              <button 
                onClick={() => {
                  fetchSystemStatus()
                  fetchEnvStatus()
                }}
                className="operation-card info"
              >
                <div className="card-icon">🔄</div>
                <div className="card-content">
                  <h3>状態更新</h3>
                  <p>システム状態を最新に更新</p>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* システム情報セクション */}
        <div className="info-section">
          <h2 className="section-title">ℹ️ システム情報</h2>
          
          <div className="info-grid">
            <div className="info-card">
              <div className="card-header">
                <span className="card-icon">🔐</span>
                <h4>API設定状態</h4>
              </div>
              <div className="card-body">
                {envStatus ? (
                  <div className="config-list">
                    <div className={`config-item ${envStatus.azureFaceApi.configured ? 'configured' : 'unconfigured'}`}>
                      <span className="config-name">Azure Face API</span>
                      <span className="config-status">{envStatus.azureFaceApi.configured ? '✅ 設定済み' : '❌ 未設定'}</span>
                    </div>
                    <div className={`config-item ${envStatus.azureSpeechApi.configured ? 'configured' : 'unconfigured'}`}>
                      <span className="config-name">Azure Speech API</span>
                      <span className="config-status">{envStatus.azureSpeechApi.configured ? '✅ 設定済み' : '❌ 未設定'}</span>
                    </div>
                    <div className={`config-item ${envStatus.microsoftGraphApi.configured ? 'configured' : 'unconfigured'}`}>
                      <span className="config-name">Microsoft Graph API</span>
                      <span className="config-status">{envStatus.microsoftGraphApi.configured ? '✅ 設定済み' : '❌ 未設定'}</span>
                    </div>
                    <div className={`config-item ${envStatus.emailSettings.configured ? 'configured' : 'unconfigured'}`}>
                      <span className="config-name">メール設定</span>
                      <span className="config-status">{envStatus.emailSettings.configured ? '✅ 設定済み' : '❌ 未設定'}</span>
                    </div>
                    <div className={`config-item ${envStatus.teamsWebhook.configured ? 'configured' : 'unconfigured'}`}>
                      <span className="config-name">Teams Webhook</span>
                      <span className="config-status">{envStatus.teamsWebhook.configured ? '✅ 設定済み' : '❌ 未設定'}</span>
                    </div>
                  </div>
                ) : (
                  <p className="loading-text">設定情報を読み込み中...</p>
                )}
              </div>
            </div>

            <div className="info-card">
              <div className="card-header">
                <span className="card-icon">⚠️</span>
                <h4>運用注意事項</h4>
              </div>
              <div className="card-body">
                <div className="notice-list">
                  <div className="notice-item">
                    <span className="notice-icon">📊</span>
                    <span className="notice-text">Azure API の使用量を定期的に確認</span>
                  </div>
                  <div className="notice-item">
                    <span className="notice-icon">💾</span>
                    <span className="notice-text">来訪ログは定期的にバックアップ</span>
                  </div>
                  <div className="notice-item">
                    <span className="notice-icon">🔐</span>
                    <span className="notice-text">顔認証データは24時間で自動削除</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 固定表示通知システム - 画面右下 */}
      {notifications.filter(n => !n.read).length > 0 && (
        <div className="fixed-notifications">
          <div className="fixed-notifications-header">
            <span className="fixed-notifications-title">📮 管理者への連絡</span>
            <div className="fixed-notifications-controls">
              <button
                onClick={() => {
                  const updatedNotifications = notifications.map(n => ({ ...n, read: true }))
                  setNotifications(updatedNotifications)
                  localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
                }}
                className="fixed-notification-btn mark-all-read"
                title="全て既読にする"
              >
                ✓
              </button>
            </div>
          </div>
          <div className="fixed-notifications-list">
            {notifications
              .filter(n => !n.read)
              .slice(0, 3)
              .map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => {
                    handleNotificationRead(notification.id)
                  }}
                  className={`fixed-notification-item ${notification.priority === 'high' ? 'high-priority' : ''}`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleNotificationDismiss(notification.id)
                    }}
                    className="fixed-notification-dismiss"
                    title="削除"
                  >
                    ×
                  </button>
                  <div className="fixed-notification-content">
                    <div className="fixed-notification-title">
                      <span className="fixed-unread-indicator"></span>
                      🔔 お客様からの連絡要請
                    </div>
                    <div className="fixed-notification-message">
                      {notification.message}
                    </div>
                    <div className="fixed-notification-meta">
                      <span>📍 {notification.location}</span>
                      <span>{new Date(notification.timestamp).toLocaleTimeString('ja-JP')}</span>
                    </div>
                  </div>
                </div>
              ))}
            {notifications.filter(n => !n.read).length > 3 && (
              <div className="fixed-notification-more">
                他 {notifications.filter(n => !n.read).length - 3} 件の未読通知
              </div>
            )}
          </div>
        </div>
      )}

      {/* トースト通知 */}
      {toastNotifications.length > 0 && (
        <div className="toast-container">
          {toastNotifications.map((toast) => (
            <div
              key={toast.id}
              className={`toast ${toast.type}`}
              onClick={() => removeToastNotification(toast.id)}
            >
              <div className="toast-icon">
                {toast.type === 'success' && '✅'}
                {toast.type === 'warning' && '⚠️'}
                {toast.type === 'error' && '❌'}
                {toast.type === 'info' && 'ℹ️'}
              </div>
              <div className="toast-message">
                {toast.message}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  removeToastNotification(toast.id)
                }}
                className="toast-close"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 確認ダイアログ */}
      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="confirm-dialog">
            <div className="dialog-header">
              <h3>
                {confirmDialog.step === 1 ? '⚠️ 重要な操作の確認' : '🔥 最終確認'}
              </h3>
            </div>
            <div className="dialog-body">
              <p>{confirmDialog.message}</p>
              {confirmDialog.step === 2 && (
                <div className="final-warning">
                  <p><strong>⚠️ この操作を実行してもよろしいですか？</strong></p>
                  <p>この操作は即座に反映されます。</p>
                </div>
              )}
            </div>
            <div className="dialog-actions">
              <button 
                onClick={handleConfirmCancel}
                className="cancel-btn"
              >
                キャンセル
              </button>
              <button 
                onClick={handleConfirmStep}
                className={`confirm-btn ${confirmDialog.step === 2 ? 'final' : 'next'}`}
              >
                {confirmDialog.step === 1 ? '次へ' : '実行'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .admin-panel {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          font-family: ${alsokTheme.typography.fontFamily.primary};
        }

        /* 改善されたヘッダー */
        .admin-header {
          background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%);
          color: white;
          padding: 16px 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.2);
          border-bottom: 3px solid #f59e0b;
        }

        .admin-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          letter-spacing: 0.5px;
        }

        .admin-header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          background: rgba(255,255,255,0.1);
          padding: 8px 16px;
          border-radius: 12px;
          backdrop-filter: blur(10px);
        }

        .user-avatar {
          font-size: 18px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        }

        .user-details {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          line-height: 1.2;
        }

        .user-role {
          font-size: 12px;
          opacity: 0.8;
          line-height: 1.2;
        }

        .login-time {
          font-size: 11px;
          opacity: 0.7;
          text-align: right;
        }

        .logout-button {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
        }

        .logout-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        .admin-content {
          padding: 32px;
          max-width: 1400px;
          margin: 0 auto;
        }

        /* セクションタイトル */
        .section-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e40af;
          margin: 0 0 24px 0;
          padding-bottom: 8px;
          border-bottom: 3px solid #3b82f6;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* 操作エリア */
        .operations-section {
          margin-bottom: 48px;
        }


        /* 統一された操作グリッド */
        .operation-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px;
        }

        /* 横3分割ステータス概要セクション */
        .status-overview-section {
          margin-bottom: 32px;
        }

        .status-overview-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 20px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 20px;
          border-radius: 16px;
          border: 2px solid #e2e8f0;
        }

        .status-area {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .status-area:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .area-title {
          font-size: 14px;
          font-weight: 700;
          color: #1e40af;
          margin: 0 0 12px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #e5e7eb;
        }

        /* 左エリア: 受付状態 */
        .reception-area {
          border-left: 4px solid #f59e0b;
        }

        /* 中央エリア: 来訪者・会議情報 */
        .visitor-area {
          border-left: 4px solid #10b981;
        }

        .compact-stats {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .compact-stat {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px;
          border-radius: 8px;
          background: #f8fafc;
        }

        .compact-stat .stat-icon {
          font-size: 20px;
          width: 28px;
          text-align: center;
        }

        .stat-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .stat-value {
          font-size: 16px;
          font-weight: 700;
          color: #1f2937;
          line-height: 1;
        }

        .stat-label {
          font-size: 11px;
          color: #6b7280;
          font-weight: 500;
        }

        /* 右エリア: システム状態 */
        .system-area {
          border-left: 4px solid #3b82f6;
        }

        .system-stats {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .system-stat {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px;
          border-radius: 8px;
          background: #f8fafc;
        }

        .system-icon {
          font-size: 18px;
          width: 28px;
          text-align: center;
        }

        .system-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
        }

        .system-label {
          font-size: 11px;
          color: #6b7280;
          font-weight: 500;
        }

        .system-status {
          font-size: 13px;
          font-weight: 600;
          line-height: 1;
        }

        .system-status.ok {
          color: #059669;
        }

        .system-status.error {
          color: #dc2626;
        }

        .reception-status-display {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .status-main {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .status-indicator-large {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 8px;
          background: #f8fafc;
        }

        .status-indicator-large.active {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.2);
        }

        .status-indicator-large.inactive {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .status-indicator-large.maintenance {
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #6b7280;
          animation: pulse 2s infinite;
        }

        .status-indicator-large.active .status-dot {
          background: #22c55e;
        }

        .status-indicator-large.inactive .status-dot {
          background: #ef4444;
        }

        .status-indicator-large.maintenance .status-dot {
          background: #f59e0b;
        }

        .status-text {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }

        .status-time {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 8px;
          background: #f1f5f9;
          border-radius: 6px;
        }

        .time-label {
          font-size: 10px;
          color: #6b7280;
          font-weight: 500;
        }

        .time-value {
          font-size: 11px;
          font-weight: 600;
          color: #374151;
        }

        .status-controls-enhanced {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 8px;
        }

        .control-btn-enhanced {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          position: relative;
          overflow: hidden;
          min-height: 48px;
          justify-content: flex-start;
        }

        .control-btn-enhanced::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.6s ease;
        }

        .control-btn-enhanced:hover:not(:disabled)::before {
          left: 100%;
        }

        .control-btn-enhanced.normal {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
          border: 2px solid #22c55e;
        }

        .control-btn-enhanced.normal:hover:not(:disabled) {
          background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 25px rgba(34, 197, 94, 0.4);
          border-color: #16a34a;
        }

        .control-btn-enhanced.normal.active {
          background: linear-gradient(135deg, #15803d 0%, #166534 100%);
          box-shadow: 0 6px 20px rgba(21, 128, 61, 0.5);
          border-color: #15803d;
        }

        .control-btn-enhanced.stop {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          border: 2px solid #ef4444;
        }

        .control-btn-enhanced.stop:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
          border-color: #dc2626;
        }

        .control-btn-enhanced.stop.active {
          background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
          box-shadow: 0 6px 20px rgba(185, 28, 28, 0.5);
          border-color: #b91c1c;
        }

        .control-btn-enhanced.maintenance {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          border: 2px solid #f59e0b;
        }

        .control-btn-enhanced.maintenance:hover:not(:disabled) {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 25px rgba(245, 158, 11, 0.4);
          border-color: #d97706;
        }

        .control-btn-enhanced.maintenance.active {
          background: linear-gradient(135deg, #b45309 0%, #92400e 100%);
          box-shadow: 0 6px 20px rgba(180, 83, 9, 0.5);
          border-color: #b45309;
          animation: maintenancePulse 2s infinite;
        }

        @keyframes maintenancePulse {
          0%, 100% {
            box-shadow: 0 6px 20px rgba(180, 83, 9, 0.5);
          }
          50% {
            box-shadow: 0 8px 25px rgba(180, 83, 9, 0.7);
          }
        }

        .control-btn-enhanced:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          background: #9ca3af !important;
          border-color: #9ca3af !important;
          box-shadow: none !important;
        }

        .control-btn-enhanced:disabled::before {
          display: none;
        }

        .btn-icon-enhanced {
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          flex-shrink: 0;
        }

        .btn-label-enhanced {
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.5px;
          flex: 1;
          text-align: center;
        }

        .control-btn-enhanced:disabled .btn-icon-enhanced {
          background: rgba(255, 255, 255, 0.1);
        }

        .control-btn-enhanced:disabled .btn-label-enhanced {
          color: rgba(255, 255, 255, 0.7);
        }

        .operation-card {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 16px;
          text-align: left;
          font-family: inherit;
          min-height: 80px;
        }

        .operation-card:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }

        .operation-card:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .operation-card.primary {
          border-color: #10b981;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .operation-card.primary:hover:not(:disabled) {
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
        }

        .operation-card.secondary {
          border-color: #3b82f6;
        }

        .operation-card.secondary:hover:not(:disabled) {
          border-color: #1d4ed8;
          background: rgba(59, 130, 246, 0.05);
        }

        .operation-card.info {
          border-color: #0ea5e9;
        }

        .operation-card.info:hover:not(:disabled) {
          border-color: #0284c7;
          background: rgba(14, 165, 233, 0.05);
        }

        .operation-card.warning {
          border-color: #f59e0b;
        }

        .operation-card.warning:hover:not(:disabled) {
          border-color: #d97706;
          background: rgba(245, 158, 11, 0.05);
        }

        .operation-card.maintenance {
          border-color: #f59e0b;
          background: rgba(245, 158, 11, 0.1);
        }

        .operation-card.maintenance:hover:not(:disabled) {
          border-color: #d97706;
          background: rgba(245, 158, 11, 0.15);
        }

        .operation-card .card-icon {
          font-size: 32px;
          min-width: 48px;
          text-align: center;
        }

        .operation-card .card-content h3 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: inherit;
        }

        .operation-card .card-content p {
          margin: 0;
          font-size: 14px;
          opacity: 0.8;
          color: inherit;
        }

        /* 参照エリア */
        .reference-section {
          margin-bottom: 48px;
        }

        .status-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .status-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .status-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }

        .status-card .card-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .status-card .card-header .card-icon {
          font-size: 20px;
        }

        .status-card .card-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .status-card .card-body {
          padding: 20px;
        }

        .status-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .status-item:last-child {
          border-bottom: none;
        }

        .status-item .label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }

        .status-item .value {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .status-item .value.success {
          color: #10b981;
        }

        .status-item .value.error {
          color: #ef4444;
        }

        .status-item .value.warning {
          color: #f59e0b;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 16px;
        }

        .stat-item {
          text-align: center;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }

        .stat-number {
          display: block;
          font-size: 20px;
          font-weight: 700;
          color: #1e40af;
          line-height: 1;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .update-time {
          font-size: 12px;
          color: #9ca3af;
          text-align: center;
          font-style: italic;
        }

        /* システム情報セクション */
        .info-section {
          margin-bottom: 32px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
        }

        .info-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .info-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .info-card .card-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .info-card .card-header .card-icon {
          font-size: 18px;
        }

        .info-card .card-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .info-card .card-body {
          padding: 20px;
        }

        .config-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .config-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #f8fafc;
          border-radius: 8px;
          border-left: 4px solid #e5e7eb;
        }

        .config-item.configured {
          border-left-color: #10b981;
          background: #f0fdf4;
        }

        .config-item.unconfigured {
          border-left-color: #ef4444;
          background: #fef2f2;
        }

        .config-name {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        .config-status {
          font-size: 14px;
          font-weight: 600;
        }

        .notice-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .notice-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #fff7ed;
          border-radius: 8px;
          border-left: 4px solid #f59e0b;
        }

        .notice-icon {
          font-size: 16px;
          min-width: 20px;
        }

        .notice-text {
          font-size: 14px;
          color: #374151;
          line-height: 1.4;
        }

        .loading-text {
          font-size: 14px;
          color: #6b7280;
          text-align: center;
          margin: 20px 0;
        }

        /* 通知バッジ */
        .notification-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
          animation: pulse 2s infinite;
        }

        .notification-icon {
          font-size: 16px;
        }

        .notification-count {
          font-size: 14px;
          font-weight: 700;
          min-width: 20px;
          text-align: center;
        }


        /* 固定表示通知システム - 画面右下 */
        .fixed-notifications {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 350px;
          max-height: 400px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          border: 2px solid #f59e0b;
          z-index: 1000;
          animation: slideInFromRight 0.3s ease-out;
        }

        .fixed-notifications-header {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          padding: 12px 16px;
          border-radius: 10px 10px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .fixed-notifications-title {
          font-size: 14px;
          font-weight: 600;
        }

        .fixed-notifications-controls {
          display: flex;
          gap: 4px;
        }

        .fixed-notification-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .fixed-notification-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .fixed-notifications-list {
          max-height: 320px;
          overflow-y: auto;
        }

        .fixed-notification-item {
          position: relative;
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #fef3c7;
          border-left: 4px solid #f59e0b;
        }

        .fixed-notification-item:last-child {
          border-bottom: none;
          border-radius: 0 0 10px 10px;
        }

        .fixed-notification-item.high-priority {
          background: #fef2f2;
          border-left-color: #ef4444;
        }

        .fixed-notification-item:hover {
          background: #fde68a;
        }

        .fixed-notification-item.high-priority:hover {
          background: #fecaca;
        }

        .fixed-notification-dismiss {
          position: absolute;
          top: 6px;
          right: 6px;
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          font-size: 14px;
          padding: 2px;
          line-height: 1;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .fixed-notification-dismiss:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .fixed-notification-content {
          padding-right: 20px;
        }

        .fixed-notification-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #374151;
        }

        .fixed-unread-indicator {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #f59e0b;
          display: inline-block;
          animation: pulse 2s infinite;
        }

        .fixed-notification-message {
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 6px;
          line-height: 1.3;
        }

        .fixed-notification-meta {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #9ca3af;
        }

        .fixed-notification-more {
          padding: 10px 16px;
          text-align: center;
          font-size: 11px;
          color: #6b7280;
          background: #f9fafb;
          border-radius: 0 0 10px 10px;
          border-top: 1px solid #e5e7eb;
        }

        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* レスポンシブデザイン */
        @media (max-width: 768px) {
          .admin-header {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .admin-header-right {
            flex-direction: column;
            gap: 12px;
          }

          .admin-content {
            padding: 16px;
          }


          .operation-grid {
            grid-template-columns: 1fr;
          }

          .status-grid {
            grid-template-columns: 1fr;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          /* 横3分割レイアウトのモバイル対応 */
          .status-overview-grid {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 15px;
          }

          .status-area {
            padding: 12px;
          }

          .area-title {
            font-size: 13px;
            margin-bottom: 8px;
          }

          .compact-stat {
            padding: 6px;
          }

          .stat-value {
            font-size: 14px;
          }

          .stat-label {
            font-size: 10px;
          }

          .system-stat {
            padding: 6px;
          }

          .control-btn-enhanced {
            min-height: 44px;
            padding: 10px 14px;
            gap: 8px;
          }

          .btn-icon-enhanced {
            width: 20px;
            height: 20px;
            font-size: 14px;
          }

          .btn-label-enhanced {
            font-size: 13px;
          }

          /* 固定通知のモバイル対応 */
          .fixed-notifications {
            bottom: 10px;
            right: 10px;
            left: 10px;
            width: auto;
            max-height: 300px;
          }

          .fixed-notification-meta {
            flex-direction: column;
            gap: 2px;
          }
        }

        /* システムモードバナー */
        .system-mode-banner {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border-left: 6px solid #f59e0b;
        }

        .system-mode-banner.maintenance {
          background: linear-gradient(90deg, rgba(245, 158, 11, 0.1) 0%, rgba(255, 255, 255, 1) 100%);
        }

        .mode-icon {
          font-size: 32px;
          animation: pulse 2s infinite;
        }

        .mode-content {
          flex: 1;
        }

        .mode-content h3 {
          margin: 0 0 8px 0;
          color: #1e40af;
          font-size: 18px;
          font-weight: 600;
        }

        .mode-content p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .mode-instruction {
          margin-top: 8px !important;
          font-size: 12px !important;
          color: #9ca3af !important;
          font-style: italic;
        }

        .mode-reset-btn {
          background: #1e40af;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .mode-reset-btn:hover {
          background: #1d4ed8;
          transform: translateY(-1px);
        }

        @keyframes pulse {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.7; 
            transform: scale(1.05); 
          }
        }

        /* 確認ダイアログ */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(5px);
        }

        .confirm-dialog {
          background: white;
          border-radius: 16px;
          padding: 0;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: dialogSlideIn 0.3s ease-out;
          overflow: hidden;
          border: 3px solid #1e40af;
        }

        .dialog-header {
          background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%);
          color: white;
          padding: 20px 24px;
          text-align: center;
        }

        .dialog-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
        }

        .dialog-body {
          padding: 24px;
          text-align: center;
        }

        .dialog-body p {
          margin: 0 0 16px 0;
          font-size: 16px;
          line-height: 1.5;
          color: #374151;
          white-space: pre-line;
        }

        .final-warning {
          background: #fff7ed;
          border: 2px solid #f59e0b;
          border-radius: 8px;
          padding: 16px;
          margin-top: 16px;
        }

        .final-warning p {
          margin: 0 0 8px 0;
          color: #d97706;
        }

        .dialog-actions {
          padding: 20px 24px;
          display: flex;
          gap: 12px;
          justify-content: center;
          background: #f8fafc;
        }

        .cancel-btn {
          background: #6b7280;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 100px;
        }

        .cancel-btn:hover {
          background: #4b5563;
          transform: translateY(-1px);
        }

        .confirm-btn {
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 100px;
          color: white;
        }

        .confirm-btn.next {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
        }

        .confirm-btn.next:hover {
          background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
          transform: translateY(-1px);
        }

        .confirm-btn.final {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          animation: pulseWarning 2s infinite;
        }

        .confirm-btn.final:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
        }

        @keyframes dialogSlideIn {
          from {
            opacity: 0;
            transform: scale(0.8) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes pulseWarning {
          0%, 100% {
            box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
          }
          50% {
            box-shadow: 0 8px 16px rgba(239, 68, 68, 0.5);
          }
        }

        /* トースト通知 */
        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10001;
          display: flex;
          flex-direction: column;
          gap: 10px;
          pointer-events: none;
        }

        .toast {
          background: white;
          border-radius: 12px;
          padding: 16px 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
          border-left: 4px solid #3b82f6;
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 300px;
          max-width: 400px;
          animation: toastSlideIn 0.3s ease-out;
          pointer-events: auto;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .toast:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
        }

        .toast.success {
          border-left-color: #10b981;
        }

        .toast.warning {
          border-left-color: #f59e0b;
        }

        .toast.error {
          border-left-color: #ef4444;
        }

        .toast.info {
          border-left-color: #3b82f6;
        }

        .toast-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .toast-message {
          flex: 1;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          line-height: 1.4;
        }

        .toast-close {
          background: none;
          border: none;
          font-size: 18px;
          color: #6b7280;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .toast-close:hover {
          background: #f3f4f6;
          color: #374151;
        }

        @keyframes toastSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>

      {/* カスタムメンテナンスダイアログ */}
      {showMaintenanceDialog && (
        <div className="maintenance-modal-overlay">
          <div className="maintenance-modal-container">
            <div className="maintenance-modal-header">
              <div className="maintenance-modal-icon">🔧</div>
              <h2 className="maintenance-modal-title">メンテナンスモード設定</h2>
            </div>
            
            <div className="maintenance-modal-content">
              <p className="maintenance-modal-description">
                システムをメンテナンスモードに切り替えます。
              </p>
              
              <div className="maintenance-modal-warning">
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <div className="warning-title">影響範囲</div>
                  <ul className="warning-list">
                    <li>お客様用受付機能が一時停止されます</li>
                    <li>会議室予約機能が利用できなくなります</li>
                    <li>ゲストアカウントでのアクセスが制限されます</li>
                  </ul>
                </div>
              </div>
              
              <div className="maintenance-message-section">
                <label className="maintenance-message-label">
                  <span className="label-icon">📝</span>
                  メンテナンス表示メッセージ
                </label>
                <textarea
                  value={tempMaintenanceMessage}
                  onChange={(e) => setTempMaintenanceMessage(e.target.value)}
                  className="maintenance-message-input"
                  placeholder="お客様に表示するメンテナンスメッセージを入力してください..."
                  rows={3}
                  maxLength={200}
                />
                <div className="message-counter">
                  {tempMaintenanceMessage.length}/200文字
                </div>
              </div>
            </div>
            
            <div className="maintenance-modal-actions">
              <button 
                onClick={handleCancelMaintenance}
                className="maintenance-modal-button cancel-button"
              >
                <span className="button-icon">❌</span>
                <span>キャンセル</span>
              </button>
              <button 
                onClick={handleConfirmMaintenance}
                className="maintenance-modal-button confirm-button"
                disabled={!tempMaintenanceMessage.trim()}
              >
                <span className="button-icon">🔧</span>
                <span>メンテナンスモード開始</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        /* メンテナンスモーダルのスタイル */
        .maintenance-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease-out;
        }

        .maintenance-modal-container {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.98) 0%, 
            rgba(248, 250, 252, 0.95) 100%);
          border-radius: 20px;
          box-shadow: 
            0 25px 80px rgba(0, 0, 0, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.4);
          max-width: 560px;
          width: 90%;
          max-height: 85vh;
          overflow: hidden;
          animation: slideInScale 0.4s ease-out;
          backdrop-filter: blur(20px);
          border: 3px solid ${alsokTheme.primary.light};
        }

        .maintenance-modal-header {
          background: linear-gradient(135deg, 
            #ff9800 0%, 
            #f57c00 100%);
          color: white;
          padding: 25px 30px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .maintenance-modal-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          animation: shimmer 3s infinite;
        }

        .maintenance-modal-icon {
          font-size: 52px;
          margin-bottom: 12px;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
          animation: rotatePulse 3s infinite;
        }

        .maintenance-modal-title {
          font-size: 26px;
          font-weight: bold;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          letter-spacing: 1px;
        }

        .maintenance-modal-content {
          padding: 30px;
        }

        .maintenance-modal-description {
          font-size: 18px;
          font-weight: 600;
          color: ${alsokTheme.neutral.darkGray};
          text-align: center;
          margin: 0 0 25px 0;
        }

        .maintenance-modal-warning {
          display: flex;
          gap: 15px;
          background: linear-gradient(135deg, 
            rgba(255, 152, 0, 0.1) 0%, 
            rgba(255, 193, 7, 0.1) 100%);
          border: 2px solid #ff9800;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 25px;
        }

        .warning-icon {
          font-size: 32px;
          animation: warningPulse 2s infinite;
          flex-shrink: 0;
        }

        .warning-content {
          flex: 1;
        }

        .warning-title {
          font-weight: bold;
          color: #f57c00;
          font-size: 18px;
          margin-bottom: 10px;
          letter-spacing: 0.5px;
        }

        .warning-list {
          margin: 0;
          padding-left: 20px;
          color: ${alsokTheme.neutral.mediumGray};
        }

        .warning-list li {
          margin-bottom: 8px;
          font-size: 14px;
          line-height: 1.5;
        }

        .maintenance-message-section {
          margin-top: 25px;
        }

        .maintenance-message-label {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          font-weight: bold;
          color: ${alsokTheme.primary.main};
          font-size: 16px;
        }

        .label-icon {
          font-size: 20px;
        }

        .maintenance-message-input {
          width: 100%;
          padding: 15px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 16px;
          font-family: inherit;
          resize: vertical;
          min-height: 80px;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.8);
          box-sizing: border-box;
        }

        .maintenance-message-input:focus {
          outline: none;
          border-color: #ff9800;
          box-shadow: 
            0 0 0 3px rgba(255, 152, 0, 0.15),
            0 4px 12px rgba(0,0,0,0.1);
          background: rgba(255, 255, 255, 1);
        }

        .message-counter {
          text-align: right;
          font-size: 12px;
          color: ${alsokTheme.neutral.mediumGray};
          margin-top: 8px;
        }

        .maintenance-modal-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          padding: 0 30px 30px 30px;
        }

        .maintenance-modal-button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px 25px;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          min-height: 55px;
          position: relative;
          overflow: hidden;
          font-family: inherit;
        }

        .maintenance-modal-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          transition: left 0.6s ease;
        }

        .maintenance-modal-button:hover::before {
          left: 100%;
        }

        .cancel-button {
          background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
          color: white;
          border: 2px solid #6c757d;
        }

        .cancel-button:hover {
          background: linear-gradient(135deg, #5a6268 0%, #495057 100%);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(108, 117, 125, 0.3);
        }

        .confirm-button {
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
          color: white;
          border: 2px solid #ff9800;
        }

        .confirm-button:hover:not(:disabled) {
          background: linear-gradient(135deg, #f57c00 0%, #ef6c00 100%);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(255, 152, 0, 0.4);
        }

        .confirm-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .confirm-button:disabled:hover {
          transform: none;
          box-shadow: none;
        }

        .button-icon {
          font-size: 18px;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInScale {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        @keyframes rotatePulse {
          0%, 100% {
            transform: scale(1) rotate(0deg);
          }
          25% {
            transform: scale(1.1) rotate(5deg);
          }
          75% {
            transform: scale(1.1) rotate(-5deg);
          }
        }

        @keyframes warningPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.15);
            opacity: 0.8;
          }
        }

        @media (max-width: 600px) {
          .maintenance-modal-container {
            width: 95%;
            margin: 20px;
          }
          
          .maintenance-modal-header {
            padding: 20px;
          }
          
          .maintenance-modal-icon {
            font-size: 44px;
          }
          
          .maintenance-modal-title {
            font-size: 22px;
          }
          
          .maintenance-modal-content {
            padding: 20px;
          }
          
          .maintenance-modal-actions {
            flex-direction: column;
            padding: 0 20px 20px 20px;
          }
          
          .maintenance-modal-button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  )
}

export default AdminPanel